import { detectGeneration } from './detect-generation';
import { readSelectedDirectory } from './sd-card-entries';
import type { PackProgress } from './pack-common';
import { packP28, p28ReimportWarning } from './pack-p28';
import { packP3 } from './pack-p3';
import { packP32 } from './pack-p32';
import { packP5 } from './pack-p5';
import {
  SdCardError,
  type PackedArchive,
  type PackWarning,
  type SdCardContainerExtension,
  type SdCardEntry,
  type SdCardGeneration,
} from './types';

const EXTENSION_BY_GENERATION: Record<
  SdCardGeneration,
  SdCardContainerExtension
> = {
  p28: 'psdat',
  p3: 'pdat',
  p32: 'pvdat',
  p5: 'pldat',
};

const FILENAME_LABEL: Record<SdCardGeneration, string> = {
  p28: 'p2_8',
  p3: 'p3',
  p32: 'p3_2',
  p5: 'p5',
};

const SERIAL_SEGMENT_RE = /^SN([A-Za-z0-9]+)$/i;
const DASH_DATE_RE = /\d{4}-\d{2}-\d{2}/;
const COMPACT_DATE_RE = /\b(20\d{2})(\d{2})(\d{2})\b/;

const findSerial = (entries: SdCardEntry[]): string | undefined => {
  for (const entry of entries) {
    for (const segment of entry.path.split('/')) {
      const match = segment.match(SERIAL_SEGMENT_RE);
      if (match) return match[1];
    }
  }
  return undefined;
};

const findLatestDate = (entries: SdCardEntry[]): string | null => {
  let latest: string | null = null;
  for (const entry of entries) {
    const dash = entry.path.match(DASH_DATE_RE);
    const compact = entry.path.match(COMPACT_DATE_RE);
    const candidate = dash
      ? dash[0]
      : compact
        ? `${compact[1]}-${compact[2]}-${compact[3]}`
        : null;
    if (candidate && (!latest || candidate > latest)) latest = candidate;
  }
  return latest;
};

const buildArchiveBaseName = (
  entries: SdCardEntry[],
  generation: SdCardGeneration,
): string => {
  const base = findSerial(entries) ?? `sdcard-${FILENAME_LABEL[generation]}`;
  const date = findLatestDate(entries);
  return date ? `${base}_${date}` : base;
};

const PACKERS: Record<
  SdCardGeneration,
  (entries: SdCardEntry[], onProgress?: PackProgress) => Promise<Blob>
> = {
  p28: packP28,
  p3: packP3,
  p32: packP32,
  p5: packP5,
};

/**
 * Reads a picked device SD-card directory and packs it into a therapy-data
 * container that SignalViewer's existing upload pipeline can open.
 */
export const packSdCard = async (
  files: File[],
  onProgress?: PackProgress,
): Promise<PackedArchive> => {
  const entries = readSelectedDirectory(files);
  if (entries.length === 0) {
    throw new SdCardError('The selected folder is empty.');
  }

  const generation = detectGeneration(entries);
  if (!generation) {
    throw new SdCardError(
      "This folder doesn't look like a Löwenstein device SD card. Expected a prisma " +
        'SMART/SOFT, prismaLINE, prisma VENT or LUISA card layout.',
    );
  }

  const blob = await PACKERS[generation](entries, onProgress);
  const extension = EXTENSION_BY_GENERATION[generation];
  const fileName = `${buildArchiveBaseName(entries, generation)}.${extension}`;

  const warnings: PackWarning[] = [];
  const reimportWarning =
    generation === 'p28' ? p28ReimportWarning(entries) : null;
  if (reimportWarning) warnings.push(reimportWarning);

  return {
    file: new File([blob], fileName, { type: 'application/octet-stream' }),
    generation,
    containerExtension: extension,
    warnings,
  };
};
